import { Box, Container, Image, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';
import { SplitHeading } from '../components/SplitHeading';
import { AudioPreviewButton } from '../components/AudioPreviewButton';

// Dark wine gradient-mesh reproducing the Figma hero background (a composition of
// ~15 heavily-blurred blobs over a #13070D base that would be ~22MB exported, so
// reproduced in CSS). Calibrated against sampled pixels of the Figma render: a
// magenta glow peaking ~rgb(120,67,96) centred upper-centre-left (~42% x, ~29% y),
// falling to near-black (~rgb(17,8,14)) at the right, top corners and bottom.
const HERO_BG =
  'radial-gradient(58% 66% at 41% 29%, rgba(132,74,106,0.95), transparent 60%),' +
  'radial-gradient(40% 48% at 61% 33%, rgba(112,63,89,0.45), transparent 60%),' +
  'radial-gradient(32% 52% at 12% 26%, rgba(82,42,60,0.5), transparent 64%)';

const PHONE = '/images/landing/hero-phone.png';

function HeroCopy() {
  const { t } = useTranslation();
  return (
    <Stack spacing={0} align="flex-start">
      <SplitHeading
        as="h1"
        text={t('landing.hero.title')}
        fontFamily="heading"
        fontWeight={700}
        fontSize="display.lg"
        lineHeight={1.2}
        letterSpacing="-0.01em"
        textTransform="capitalize"
        color="white"
      />
      <Text mt="14px" fontSize="md" lineHeight="25px" color="whiteAlpha.700" maxW="583px">
        {t('landing.hero.subtitle')}
      </Text>
      <CtaButton
        to="/onboarding"
        mt="40px"
        bg="onboarding.cream"
        color="onboarding.plum"
        borderRadius="pill"
        height="48px"
        px="28px"
        fontSize="md"
        fontWeight={600}
        withArrow
        _hover={{ bg: 'white' }}
        trackEventName="cta_click"
        trackProps={{ section: 'hero' }}
      >
        {t('landing.hero.startTrial')}
      </CtaButton>
      <Text mt="10px" fontSize="sm" color="whiteAlpha.600">
        {t('landing.hero.trialTerms')}
      </Text>
      <AudioPreviewButton />
    </Stack>
  );
}

interface FloatingCardProps {
  title: string;
  body: string;
  left: string;
  top: string;
}

function FloatingCard({ title, body, left, top }: FloatingCardProps) {
  return (
    <Box
      position="absolute"
      left={left}
      top={top}
      // Figma: cream fill at 10% + Glass effect over the dark hero → frosted dark card
      bg="rgba(251,245,239,0.10)"
      backdropFilter="blur(24px)"
      border="1px solid"
      borderColor="onboarding.midRose2"
      borderRadius="20px"
      px="20px"
      py="13px"
      boxShadow="0 10px 30px rgba(0,0,0,0.25)"
    >
      <Text fontSize="md" fontWeight={600} color="white" whiteSpace="nowrap">
        {title}
      </Text>
      <Text fontSize="sm" fontWeight={300} color="whiteAlpha.800" whiteSpace="nowrap">
        {body}
      </Text>
    </Box>
  );
}

export function Hero() {
  const { t } = useTranslation();
  return (
    <Box
      as="section"
      id="hero"
      position="relative"
      overflow="hidden"
      bg="#13070D"
      color="white"
      mt={{ base: '-68px', xl: '-88px' }}
      borderBottomRadius={{ base: '28px', md: '48px', xl: '70px' }}
    >
      <Box position="absolute" inset={0} backgroundImage={HERO_BG} pointerEvents="none" />

      {/* Desktop: pixel-exact 1440 stage (absolute positions match the Figma) */}
      <Box display={{ base: 'none', xl: 'block' }} position="relative" w="1440px" h="755px" mx="auto">
        <Box position="absolute" left="134px" top="208px" w="681px">
          <HeroCopy />
        </Box>
        <Image src={PHONE} alt="" position="absolute" left="858px" top="110px" w="444px" draggable={false} />
        {/* Callout lines linking each floating card to the phone (Figma). */}
        <Image src="/images/landing/hero-connector-2.png" alt="" position="absolute" left="974px" top="147px" w="94px" draggable={false} />
        <Image src="/images/landing/hero-connector-1.png" alt="" position="absolute" left="828px" top="557px" w="140px" draggable={false} />
        <Image src="/images/landing/hero-connector-3.png" alt="" position="absolute" left="1113px" top="490px" w="99px" draggable={false} />
        {/* Filled dots terminating each connector on the phone (Figma: 6px #573447). */}
        {[
          { left: '1068px', top: '201px' },
          { left: '1114px', top: '492px' },
          { left: '968px', top: '557px' },
        ].map((d, i) => (
          <Box
            key={i}
            position="absolute"
            left={d.left}
            top={d.top}
            transform="translate(-50%, -50%)"
            w="7px"
            h="7px"
            borderRadius="full"
            bg="onboarding.midRose2"
            pointerEvents="none"
          />
        ))}
        <FloatingCard left="748px" top="125px" title={t('landing.hero.chips.readyTitle')} body={t('landing.hero.chips.readyBody')} />
        <FloatingCard left="615px" top="563px" title={t('landing.hero.nowPlaying')} body={t('landing.hero.nowPlayingTrack')} />
        <FloatingCard left="1213px" top="548px" title={t('landing.hero.chips.autoTitle')} body={t('landing.hero.chips.autoBody')} />
      </Box>

      {/* Tablet / mobile: stacked flow */}
      <Container
        display={{ base: 'block', xl: 'none' }}
        maxW="720px"
        position="relative"
        pt={{ base: '96px', md: '128px' }}
        pb={{ base: 16, md: 24 }}
      >
        <HeroCopy />
        <Image src={PHONE} alt="" maxW={{ base: '260px', md: '320px' }} mt={14} mx="auto" draggable={false} />
      </Container>
    </Box>
  );
}
